import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, AlertCircle, Loader2, Trophy } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DnaBackground } from "@/components/DnaBackground";
import { Navigation } from "@/components/Navigation";
import UserStrengthsView from "@/components/UserStrengthsView";
import { getUserData, usersAPI } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

interface DirectoryProfileUser {
  id: string;
  name: string;
  category: "student" | "graduate";
  profileImage?: string | null;
  profileImageDisplayUrl?: string | null;
}

interface DirectoryProfileStrength {
  rank: number;
  theme_name: string;
  name?: string;
  nameAr?: string | null;
  domain?: string | null;
  description?: string | null;
  shortDescription?: string | null;
  score?: number;
}

interface DirectoryProfileData {
  user: DirectoryProfileUser;
  status: "success" | "no_report" | "pending" | "needs_review" | "failed";
  message?: string;
  strengths: DirectoryProfileStrength[];
}

export default function DirectoryUserProfile() {
  const { userId } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [viewer] = useState(() => getUserData());
  const [profile, setProfile] = useState<DirectoryProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isRtl = i18n.language === "ar";
  const backPath = viewer?.role === "admin" ? "/admin" : "/directory";
  const backLabel = viewer?.role === "admin" ? t("admin.backToAdminUsers") : t("admin.backToDirectory");

  useEffect(() => {
    if (!viewer) {
      navigate("/auth");
      return;
    }

    if (!userId) {
      navigate(backPath);
      return;
    }

    const loadProfile = async () => {
      setIsLoading(true);
      try {
        const response = await usersAPI.getDirectoryUserProfile(userId);
        setProfile(response.data);
      } catch (error: any) {
        toast({
          title: t("errors.loadProfileFailed"),
          description: getLocalizedErrorMessage(error, "errors.loadProfileFailed"),
          variant: "destructive",
        });

        if (error.status === 403 || error.status === 404) {
          navigate(backPath);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [backPath, navigate, toast, userId, viewer]);

  const categoryLabel = profile?.user.category === "graduate"
    ? t("admin.categoryGraduate")
    : t("admin.categoryStudent");

  const statusMessage = profile?.message || t("profile.noStrengthsYet", { defaultValue: "No results yet" });

  return (
    <div className="min-h-screen overflow-x-hidden" dir={isRtl ? "rtl" : "ltr"}>
      <DnaBackground />
      <Navigation user={viewer} />

      <main className="pt-24 px-4 sm:px-6 pb-12">
        <div className="container mx-auto max-w-5xl min-w-0 space-y-6">
          <Button variant="ghost" className="gap-2 px-0" asChild>
            <Link to={backPath} dir={isRtl ? "rtl" : "ltr"}>
              <ArrowLeft className={`h-4 w-4 ${isRtl ? "rotate-180" : ""}`} />
              {backLabel}
            </Link>
          </Button>

          {isLoading ? (
            <Card className="flex items-center justify-center gap-3 p-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              {t("directoryProfile.loading")}
            </Card>
          ) : profile ? (
            <>
              <Card>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
                    <Avatar className="h-20 w-20 sm:h-24 sm:w-24">
                      <AvatarImage
                        src={profile.user.profileImageDisplayUrl || profile.user.profileImage || undefined}
                        alt={profile.user.name}
                      />
                      <AvatarFallback className="bg-primary text-3xl text-primary-foreground">
                        {profile.user.name?.charAt(0).toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-3">
                        <h1 className="break-words text-2xl font-bold tracking-normal sm:text-3xl">
                          {profile.user.name}
                        </h1>
                        <Badge variant="secondary">{categoryLabel}</Badge>
                      </div>
                      <p className="mt-2 text-muted-foreground">
                        {t("directoryProfile.readOnlySubtitle")}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {profile.status === "success" && profile.strengths.length === 34 ? (
                <UserStrengthsView
                  embedded
                  open
                  onOpenChange={() => undefined}
                  userName={profile.user.name}
                  strengths={[...profile.strengths]
                    .sort((a, b) => a.rank - b.rank)
                    .map((strength) => ({
                      rank: strength.rank,
                      name: strength.name || strength.theme_name,
                      nameAr: strength.nameAr || undefined,
                      domain: strength.domain || "Other",
                      description: strength.description || undefined,
                      shortDescription: strength.shortDescription || undefined,
                      score: strength.score,
                    }))}
                />
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-primary" />
                      {t("directoryProfile.cliftonStrengthsTitle")}
                    </CardTitle>
                    <CardDescription>
                      {t("directoryProfile.cliftonStrengthsSubtitle")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-start gap-3 rounded-md border border-dashed border-border bg-muted/20 p-4">
                      <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-foreground">{statusMessage}</p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {t("directoryProfile.availabilityHint")}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : null}
        </div>
      </main>
    </div>
  );
}
